import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';

function SessionsSidebar({ isOpen, setIsOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();
  const { isOnboarded } = useStripe();
  
  const [stats, setStats] = useState({
    workshopsCount: 0,
    bookingsCount: 0,
    revenue: 0
  });

  useEffect(() => {
    if (currentUser) {
      fetchStats();
    }
  }, [currentUser]);

  const fetchStats = async () => {
    try {
      // Fetch workshop and booking stats
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const workshopsQuery = query(
        collection(db, 'sessions_workshops'),
        where('instructorId', '==', currentUser.uid)
      );
      
      const bookingsQuery = query(
        collection(db, 'sessions_bookings'),
        where('instructorId', '==', currentUser.uid)
      );
      
      const [workshopsSnapshot, bookingsSnapshot] = await Promise.all([
        getDocs(workshopsQuery),
        getDocs(bookingsQuery)
      ]);
      
      const workshops = workshopsSnapshot.docs.map(doc => doc.data());
      const bookings = bookingsSnapshot.docs.map(doc => doc.data());
      
      const confirmedBookings = bookings.filter(b => b.status === 'confirmed');
      const totalRevenue = confirmedBookings.reduce((sum, booking) => sum + (booking.totalAmount || 0), 0);
      
      setStats({
        workshopsCount: workshops.length,
        bookingsCount: confirmedBookings.length,
        revenue: totalRevenue
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const isActive = (path) => location.pathname === path;

  const menuItems = [
    { path: '/dashboard', label: 'DASHBOARD' },
    { path: '/profile', label: 'PROFILE' },
    { path: '/create-workshop', label: 'WORKSHOPS' },
  ];

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      <div className="p-4 sm:p-6 flex-1">
        {/* Logo */}
        <div className="flex items-center space-x-2 mb-6">
          <h1 className="text-sm tracking-wider text-white font-medium">
            S E S S I O N S
          </h1>
          <span className="px-1.5 py-0.5 text-[8px] bg-klorah-primary/10 rounded text-klorah-primary font-medium">
            BY KLORAH
          </span>
        </div>

        {/* Revenue Display */}
        <div className="mb-6 p-3 glass-panel-subtle">
          <p className="text-xs text-klorah-text-light mb-1 tracking-wider">
            TOTAL REVENUE
          </p>
          <p className="text-base font-medium text-klorah-primary">
            ${stats.revenue.toFixed(2)}
          </p>
        </div>

        {/* Stats */}
        <div className="space-y-6">
          <div className="space-y-1">
            <div className="flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer hover:bg-klorah-card/50 transition-colors group">
              <span className="text-sm font-medium text-klorah-text group-hover:text-klorah-primary transition-colors tracking-wider">
                WORKSHOPS
              </span>
              <span className="text-xs text-klorah-text group-hover:text-klorah-primary transition-colors">
                {stats.workshopsCount}
              </span>
            </div>
            
            <div className="flex items-center justify-between py-2 px-3 rounded-lg cursor-pointer hover:bg-klorah-card/50 transition-colors group">
              <span className="text-sm font-medium text-klorah-text group-hover:text-klorah-primary transition-colors tracking-wider">
                BOOKINGS
              </span>
              <span className="text-xs text-klorah-text group-hover:text-klorah-primary transition-colors">
                {stats.bookingsCount}
              </span>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <p className="text-sm text-klorah-text-light mb-2 tracking-wider">
              NAVIGATION
            </p>
            <div className="space-y-1">
              {menuItems.map(({ path, label }) => (
                <button
                  key={path}
                  onClick={() => {
                    navigate(path);
                    setIsOpen(false);
                  }}
                  className={`flex items-center w-full py-2 px-3 rounded-lg text-sm font-medium tracking-wider transition-colors ${
                    isActive(path)
                      ? 'bg-klorah-card text-klorah-primary'
                      : 'text-klorah-text hover:text-klorah-primary hover:bg-klorah-card/30'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Status */}
      {!isOnboarded && (
        <div className="p-4 sm:p-6 border-t border-klorah-border">
          <div className="glass-panel-subtle p-3 border border-yellow-500/30">
            <p className="text-xs text-yellow-400 mb-2 tracking-wider">
              PAYMENT SETUP REQUIRED
            </p>
            <p className="text-xs text-klorah-text mb-3">
              Complete Stripe setup to receive payments
            </p>
            <button
              onClick={() => {
                navigate('/dashboard');
                setIsOpen(false);
              }}
              className="w-full py-2 text-xs font-medium bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 rounded-xl hover:bg-yellow-500/30 transition-colors"
            >
              COMPLETE SETUP
            </button>
          </div>
        </div>
      )}

      {/* User Section */}
      <div className="p-4 sm:p-6 border-t border-klorah-border">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-klorah-primary to-klorah-secondary rounded-full flex items-center justify-center">
            <span className="text-black text-sm font-medium">
              {currentUser?.email?.[0]?.toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {currentUser?.email}
            </p>
            <p className="text-xs text-klorah-text-light">
              Instructor
            </p>
          </div>
        </div>
        
        <button
          onClick={logout}
          className="w-full py-2.5 text-sm font-medium bg-klorah-card border border-klorah-border text-klorah-text hover:text-white hover:bg-klorah-border rounded-xl transition-all tracking-wider"
        >
          SIGN OUT
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:block lg:w-64 bg-klorah-dark border-r border-klorah-border h-screen">
        <SidebarContent />
      </div>

      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-50 flex lg:hidden"
          onClick={() => setIsOpen(false)}
        >
          <div className="absolute inset-0 bg-black/50" />
          <nav
            className={`relative w-64 bg-klorah-dark border-r border-klorah-border h-full transition-transform duration-300 ${
              isOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <SidebarContent />
          </nav>
        </div>
      )}
    </>
  );
}

export default SessionsSidebar; 